<template>
  <div class="chart-bar-stacked" :class="!chartSettings.legendPosition || (chartSettings.legendPosition && chartSettings.legendPosition === 'bottom') ? 'bottom' : ''">
    <div ref="legendwrapper" class="legendwrapper" :style="{ height: chartSettings.chartHeight ? chartSettings.chartHeight + 'px' : '400px' }">
      <div ref="legenddiv" class="legenddiv" :style="{ minHeight: chartSettings.chartHeight ? chartSettings.chartHeight - 10 + 'px' : '390px' }"></div>
    </div>
    <div ref="chartdiv" class="chartdiv" :style="{ height: chartSettings.chartHeight ? chartSettings.chartHeight + 'px' : '400px' }"></div>
  </div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import * as _ from 'underscore'
import { mapGetters } from 'vuex'

export default {
  name: 'chartBarStackedHundredPercentRender',
  props: {
    chartKey: { type: String, required: true },
    fromDialog: { type: Boolean, required: false }
  },
  data () {
    return {
      chartSettings: {},
      chartData: []
    }
  },
  computed: {
    ...mapGetters(['translation']),
    chartObj: {
      get () { return this.$store.getters.getChartByKey(this.chartKey) }
    }
  },
  mounted () {
    let self = this
    this.chartSettings = this.chartObj.chartSettings
    this.chartData = this.chartObj.data.result

    // check and reduce data if not in dialog
    if (!this.fromDialog && this.chartData.length > (this.chartObj.chartSettings.displayLength || 10)) {
      const reducedData = this.chartData.filter((data, index) => index < (this.chartObj.chartSettings.displayLength || 10))
      this.chartData = reducedData
    }

    let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart)

    // Add data
    chart.data = this.chartData

    chart.paddingRight = 20
    chart.responsive.enabled = true

    let categoryAxis
    let valueAxis
    // Create axes & heck for chart rotation
    if (self.chartSettings.rotate) {
      categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
      categoryAxis.renderer.inversed = true
      valueAxis = chart.xAxes.push(new am4charts.ValueAxis())
      valueAxis.renderer.opposite = true
    } else {
      categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis())
      valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
    }

    categoryAxis.dataFields.category = 'name'
    categoryAxis.renderer.grid.template.location = 0
    categoryAxis.renderer.minGridDistance = self.chartSettings.gridDistance || 25

    valueAxis.min = 0
    valueAxis.max = 100
    valueAxis.strictMinMax = true
    valueAxis.calculateTotals = true

    function createSeries (field, translatedField) {
      let series = chart.series.push(new am4charts.ColumnSeries())
      series.columns.template.width = am4core.percent(self.chartSettings.barsWidth || 50) // column width
      if (self.chartSettings.tooltipText) {
        let tooltipText = self.chartSettings.tooltipText
        series.columns.template.tooltipText =
          `${tooltipText[0]}: [bold]{name}[/]\n ${tooltipText[1]}: [bold]${translatedField}[/]\n ${tooltipText[2]}: [bold]{valueY} ({valueY.totalPercent.formatNumber('#.00')}%)[/]`
      } else {
        series.columns.template.tooltipText = `${translatedField}: [bold]{valueY} ({valueY.totalPercent.formatNumber('#.00')}%)[/]`
      }
      series.name = translatedField
      series.dataFields.categoryX = 'name'
      series.dataFields.valueY = field
      series.dataFields.valueYShow = 'totalPercent'
      series.stacked = true

      series.tooltip.getFillFromObject = false
      series.tooltip.background.fill = am4core.color('#383336')
      series.tooltip.background.stroke = am4core.color('#383336')
      series.tooltip.pointerOrientation = 'right'
      series.tooltip.label.fill = am4core.color('#e0dbde')
      series.tooltip.label.textAlign = 'middle'
      series.tooltip.label.paddingBottom = '10'
      series.tooltip.label.paddingTop = '10'

      // let bullet = series.bullets.push(new am4charts.LabelBullet())
      // bullet.interactionsEnabled = false
      // bullet.label.text = "{valueY.totalPercent.formatNumber('#.00')}%"
      // bullet.label.fill = am4core.color('#ffffff')
      // bullet.locationY = 0.5

      // Category labels
      let categoryAxisLabel = categoryAxis.renderer.labels.template
      categoryAxisLabel.rotation = self.chartSettings.labelRotation || 0
      categoryAxisLabel.horizontalCenter = self.chartSettings.labelHorizontalCenter || 'middle'
      categoryAxisLabel.verticalCenter = self.chartSettings.labelVerticalCenter || 'middle'
      categoryAxisLabel.textAlign = self.chartSettings.textAlign || null
      self.chartSettings.truncate !== undefined
        ? categoryAxisLabel.truncate = self.chartSettings.truncate
        : categoryAxisLabel.truncate = true
      categoryAxisLabel.tooltip = new am4core.Tooltip()
      categoryAxisLabel.tooltip.pointerOrientation = 'up'
      categoryAxisLabel.tooltipText = '{category}'
      // categoryAxisLabel.maxWidth = 120 // if no auto max-width below
      // alternative to maxWidth
      categoryAxis.events.on('sizechanged', ev => {
        let axis = ev.target
        if (!self.chartSettings.rotate) {
          axis.renderer.labels.template.maxWidth = self.chartSettings.maxLabelWidth || (axis.pixelWidth / (axis.endIndex - axis.startIndex))
        } else {
          axis.renderer.labels.template.maxWidth = self.chartSettings.maxLabelWidth || chart.pixelWidth / 2
        }
      })

      return series
    }

    let allKeys = []
    const totalValue = {}
    this.chartData.forEach(item => {
      _.keys(item).forEach(itemKey => {
        if (itemKey !== 'name' && itemKey !== 'sort_column' && itemKey !== 'total') {
          if (!_.includes(allKeys, itemKey)) {
            allKeys.push(itemKey)
            totalValue[itemKey] = item[itemKey]
          } else {
            totalValue[itemKey] += item[itemKey]
          }
        }
      })
    })
    allKeys.sort((a, b) => totalValue[b] - totalValue[a])

    if (this.chartSettings.keysLimit) {
      allKeys = allKeys.slice(0, this.chartSettings.keysLimit)
    }

    _.each(allKeys, key => {
      if (self.chartSettings.translation) {
        createSeries(key, self.translation(`chart.data.value.${key}`))
      } else {
        createSeries(key, key)
      }
    })

    function resizeLegend () {
      setTimeout(() => {
        self.$refs.legenddiv.style.height = chart.legend.contentHeight + 'px'
        if (chart.legend.position === 'bottom') {
          self.$refs.legendwrapper.style.height = chart.legend.contentHeight + 10 + 'px'
        }
      }, 1000)
    }

    if (this.chartSettings.legend) {
      chart.legend = new am4charts.Legend()
      let legendContainer = am4core.create(this.$refs.legenddiv, am4core.Container)
      chart.legend.parent = legendContainer
      chart.legend.position = this.chartSettings.legendPosition || 'bottom'
      chart.legend.itemContainers.template.paddingTop = 5
      chart.legend.itemContainers.template.paddingBottom = 5
      chart.legend.itemContainers.template.marginBottom = 35
      chart.legend.useDefaultMarker = true
      let marker = chart.legend.markers.template.children.getIndex(0)
      marker.cornerRadius(12, 12, 12, 12)

      if (chart.legend.position === 'bottom') {
        legendContainer.width = am4core.percent(100)
      }

      // chart.events.on('datavalidated', resizeLegend)
      // chart.events.on('maxsizechanged', resizeLegend)

      chart.legend.events.on('datavalidated', resizeLegend)
      chart.legend.events.on('maxsizechanged', resizeLegend)
    }

    // chart.scrollbarX = new am4core.Scrollbar()

    this.chart = chart
  },
  methods: {},
  beforeDestroy () {
    if (this.chart) {
      this.chart.dispose()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  .chart-bar-stacked {
    display: flex;
    .chartdiv {
      width: 100%;
    }
    .legendwrapper {
      max-width: 250px;
      overflow-y: auto;
      margin-left: 10px;
      padding-left: 5px;
      // border: 1px dotted #c99;
    }
    &.bottom {
      flex-direction: column-reverse;
      .legendwrapper {
        width: 100%;
        max-width: none;
        margin-left: 0;
        padding-left: 0;
        margin-top: 10px;
        .legenddiv {
          min-height: unset !important;
        }
      }
    }
  }
</style>
